import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useEvent, useLazyCommand } from '@soap/modules';
import { ArrowLeft, Plus, Trash2 } from 'lucide-react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../ui/card';
import {
  parseEducationSystemDefinitionsEvent,
  type EducationSystemDefinition,
  type PlacementInUse
} from '../../utils/educationSystemDefinitions';

function newClientId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  return `id-${Math.random().toString(36).slice(2, 11)}`;
}

/** Persisted key stage / year ids (C314); must be a real UUID for the server. */
function newStableEntityGuid(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  throw new Error('Your browser must support crypto.randomUUID() to edit education system structure.');
}

function formatYearWithTypicalAges(y: {
  yearName: string;
  typicalAgeMinYears?: number | null;
  typicalAgeMaxYears?: number | null;
}): string {
  const min = y.typicalAgeMinYears;
  const max = y.typicalAgeMaxYears;
  if (min != null && max != null) return `${y.yearName} (ages ${min}–${max})`;
  if (min != null) return `${y.yearName} (age ${min})`;
  if (max != null) return `${y.yearName} (age ${max})`;
  return y.yearName;
}

function ieq(a: string, b: string): boolean {
  return a.trim().toLowerCase() === b.trim().toLowerCase();
}

function placementListHasPair(
  def: EducationSystemDefinition | undefined,
  placements: PlacementInUse[],
  keyStageName: string,
  yearName: string
): boolean {
  const y = yearName.trim();
  if (!y || !def) return false;
  const ks = def.keyStages.find((k) => ieq(k.name, keyStageName));
  if (!ks?.keyStageId) return false;
  const year = ks.years.find((x) => ieq(x.yearName, y));
  if (!year) return false;
  return placements.some(
    (p) => p.keyStageId === ks.keyStageId && p.yearId === year.yearId
  );
}

function placementListHasKeyStage(
  def: EducationSystemDefinition | undefined,
  placements: PlacementInUse[],
  keyStageName: string
): boolean {
  if (!keyStageName.trim() || !def) return false;
  const ks = def.keyStages.find((k) => ieq(k.name, keyStageName));
  if (!ks?.keyStageId) return false;
  return placements.some((p) => p.keyStageId === ks.keyStageId);
}

function tenantCustomGuidFromKey(key: string): string | null {
  const p = 'tenant-custom:';
  const t = key.trim();
  if (!t.toLowerCase().startsWith(p)) return null;
  return t.slice(p.length).trim();
}

type EditYear = {
  clientId: string;
  label: string;
  serverYearId?: string;
  typicalAgeMinYears?: string;
  typicalAgeMaxYears?: string;
};
type EditBand = { clientId: string; name: string; serverKeyStageId?: string; years: EditYear[] };

export function EducationSystemSettings() {
  const [defs, setDefs] = useState<EducationSystemDefinition[]>([]);
  const [loadDefs] = useLazyCommand('C313v1_GetEducationSystemDefinitions');
  const [sendAdd] = useLazyCommand('C312v1_AddTenantEducationSystem');
  const [sendSave] = useLazyCommand('C314v1_SaveTenantEducationSystemStructure');
  const [sendUpdate] = useLazyCommand('C319v1_UpdateTenantEducationSystem');
  const [sendDelete] = useLazyCommand('C315v1_DeleteTenantEducationSystem');

  const [newName, setNewName] = useState('');
  const [newNotes, setNewNotes] = useState('');
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [editDisplayName, setEditDisplayName] = useState('');
  const [editBands, setEditBands] = useState<EditBand[]>([]);

  const refresh = useCallback(() => {
    void loadDefs({});
  }, [loadDefs]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEvent({
    eventName: 'E313v1_GotEducationSystemDefinitions',
    onEventReceived: (e: unknown) => {
      setDefs(parseEducationSystemDefinitionsEvent(e));
    }
  });

  useEvent({
    eventName: 'E312v1_TenantEducationSystemAdded',
    onEventReceived: () => refresh()
  });

  const builtInFrameworks = defs.filter((d) => d.isBuiltIn);
  const custom = defs.filter((d) => !d.isBuiltIn);

  const editingDef = useMemo(
    () => (editingKey ? defs.find((x) => x.key === editingKey) : undefined),
    [defs, editingKey]
  );
  const editingUsagePlacements = useMemo((): PlacementInUse[] => editingDef?.placementsInUse ?? [], [editingDef]);

  const startEdit = (key: string) => {
    const d = defs.find((x) => x.key === key);
    if (!d || d.isBuiltIn) return;
    setEditingKey(key);
    setEditDisplayName(d.displayName);
    setEditBands(
      d.keyStages.length
        ? d.keyStages.map((ks) => {
            const years = ks.years.length
              ? ks.years.map((y) => ({
                  clientId: newClientId(),
                  label: y.yearName,
                  serverYearId: y.yearId,
                  typicalAgeMinYears:
                    y.typicalAgeMinYears != null ? String(y.typicalAgeMinYears) : '',
                  typicalAgeMaxYears:
                    y.typicalAgeMaxYears != null ? String(y.typicalAgeMaxYears) : ''
                }))
              : [];
            return {
              clientId: newClientId(),
              name: ks.name,
              serverKeyStageId: ks.keyStageId,
              years
            };
          })
        : [
            {
              clientId: newClientId(),
              name: '',
              serverKeyStageId: newStableEntityGuid(),
              years: []
            }
          ]
    );
  };

  const cancelEdit = () => {
    setEditingKey(null);
    setEditDisplayName('');
    setEditBands([]);
  };

  const saveEdit = async () => {
    if (!editingKey) return;
    const id = tenantCustomGuidFromKey(editingKey);
    if (!id) return;
    const def = defs.find((x) => x.key === editingKey);
    if (!def) return;

    const nameTrim = editDisplayName.trim();
    if (!nameTrim) {
      window.alert('Display name is required.');
      return;
    }

    const bands = editBands
      .map((b) => ({
        name: b.name.trim(),
        serverKeyStageId: b.serverKeyStageId,
        years: b.years
          .map((y) => ({
            label: y.label.trim(),
            serverYearId: y.serverYearId,
            typicalAgeMinYears: parseOptionalAge(y.typicalAgeMinYears),
            typicalAgeMaxYears: parseOptionalAge(y.typicalAgeMaxYears)
          }))
          .filter((y) => y.label)
      }))
      .filter((b) => b.name);

    if (bands.length === 0) {
      window.alert('Add at least one key stage with a name.');
      return;
    }

    for (const b of bands) {
      for (const y of b.years) {
        if (
          y.typicalAgeMinYears != null &&
          y.typicalAgeMaxYears != null &&
          y.typicalAgeMinYears > y.typicalAgeMaxYears
        ) {
          window.alert(
            `Typical age min for "${y.label}" must be less than or equal to max.`
          );
          return;
        }
        for (const age of [y.typicalAgeMinYears, y.typicalAgeMaxYears]) {
          if (age != null && (age < 1 || age > 25)) {
            window.alert(`Typical ages for "${y.label}" must be between 1 and 25.`);
            return;
          }
        }
      }
    }

    try {
      if (nameTrim !== def.displayName.trim()) {
        await sendUpdate({
          C319_SystemId: id,
          C319_DisplayName: nameTrim
        });
      }
      await sendSave({
        C314_SystemId: id,
        C314_KeyStageBands: bands.map((b) => ({
          C314_KeyStageId: b.serverKeyStageId,
          C314_Name: b.name,
          C314_Years: b.years.map((y) => ({
            C314_YearId: y.serverYearId,
            C314_YearName: y.label,
            C314_TypicalAgeMinYears: y.typicalAgeMinYears ?? undefined,
            C314_TypicalAgeMaxYears: y.typicalAgeMaxYears ?? undefined
          }))
        }))
      });
      cancelEdit();
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : typeof e === 'string' ? e : 'Save failed';
      window.alert(msg);
    }
  };

  function parseOptionalAge(raw: string | undefined): number | null {
    const t = (raw ?? '').trim();
    if (!t) return null;
    const n = Number(t);
    if (!Number.isFinite(n) || !Number.isInteger(n)) return null;
    return n;
  }

  const handleDelete = async (def: EducationSystemDefinition) => {
    if (def.systemKeyInUse) return;
    const id = tenantCustomGuidFromKey(def.key);
    if (!id) return;
    if (
      !window.confirm(
        'Delete this education system? This cannot be undone if no subjects use it.'
      )
    ) {
      return;
    }
    try {
      await sendDelete({ C315_SystemId: id });
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : typeof e === 'string' ? e : 'Delete failed';
      window.alert(msg);
    }
  };

  const addCustom = async () => {
    if (!newName.trim()) return;
    try {
      await sendAdd({
        C312_DisplayName: newName.trim(),
        C312_Notes: newNotes.trim() || undefined
      });
      setNewName('');
      setNewNotes('');
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : typeof e === 'string' ? e : 'Add failed';
      window.alert(msg);
    }
  };

  const updateBandName = (bandIdx: number, name: string) => {
    setEditBands((prev) => {
      const next = [...prev];
      next[bandIdx] = { ...next[bandIdx], name };
      return next;
    });
  };

  const updateYearLabel = (bandIdx: number, yearIdx: number, label: string) => {
    setEditBands((prev) => {
      const next = [...prev];
      const band = { ...next[bandIdx], years: [...next[bandIdx].years] };
      band.years[yearIdx] = { ...band.years[yearIdx], label };
      next[bandIdx] = band;
      return next;
    });
  };

  const updateYearAge = (
    bandIdx: number,
    yearIdx: number,
    field: 'typicalAgeMinYears' | 'typicalAgeMaxYears',
    value: string
  ) => {
    setEditBands((prev) => {
      const next = [...prev];
      const band = { ...next[bandIdx], years: [...next[bandIdx].years] };
      band.years[yearIdx] = { ...band.years[yearIdx], [field]: value };
      next[bandIdx] = band;
      return next;
    });
  };

  const addYearRow = (bandIdx: number) => {
    setEditBands((prev) => {
      const next = [...prev];
      const band = {
        ...next[bandIdx],
        years: [
          ...next[bandIdx].years,
          {
            clientId: newClientId(),
            label: '',
            serverYearId: newStableEntityGuid(),
            typicalAgeMinYears: '',
            typicalAgeMaxYears: ''
          }
        ]
      };
      next[bandIdx] = band;
      return next;
    });
  };

  const removeYearRow = (bandIdx: number, yearIdx: number) => {
    const band = editBands[bandIdx];
    const y = band.years[yearIdx];
    const yearInUse = placementListHasPair(editingDef, editingUsagePlacements, band.name, y.label);
    if (yearInUse) return;
    setEditBands((prev) => {
      const next = [...prev];
      const b = { ...next[bandIdx], years: next[bandIdx].years.filter((_, i) => i !== yearIdx) };
      next[bandIdx] = b;
      return next;
    });
  };

  const removeBandRow = (bandIdx: number) => {
    const band = editBands[bandIdx];
    if (placementListHasKeyStage(editingDef, editingUsagePlacements, band.name)) return;
    setEditBands((prev) => prev.filter((_, i) => i !== bandIdx));
  };

  const addBandRow = () => {
    setEditBands((prev) => [
      ...prev,
      {
        clientId: newClientId(),
        name: '',
        serverKeyStageId: newStableEntityGuid(),
        years: []
      }
    ]);
  };

  return (
    <div className="space-y-8 max-w-3xl">
      <p className="text-gray-600">
        You can add custom systems and define key stages and years for use when creating custom subjects.
      </p>

      <Card>
        <CardHeader>
          <CardTitle>Custom education systems</CardTitle>
          <CardDescription>Add a system, then define key stages and year labels.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-col sm:flex-row gap-2 sm:items-end">
            <div className="flex-1">
              <label className="text-sm font-medium text-gray-700">Name</label>
              <Input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="e.g. Montessori bands" />
            </div>
            <div className="flex-1">
              <label className="text-sm font-medium text-gray-700">Notes (optional)</label>
              <Input value={newNotes} onChange={(e) => setNewNotes(e.target.value)} />
            </div>
            <Button type="button" onClick={() => void addCustom()}>
              <Plus className="h-4 w-4 mr-1" />
              Add
            </Button>
          </div>

          <ul className="space-y-6 list-none pl-0">
            {custom.map((d) => {
              const isFlipped = editingKey === d.key;
              return (
                <li
                  key={d.key}
                  className={`relative border rounded-lg border-gray-200 bg-white shadow-sm transition-all duration-500 ${
                    isFlipped ? 'h-auto' : 'min-h-[140px]'
                  }`}
                >
                  <div
                    className={`transition-transform duration-500 transform-style-preserve-3d ${
                      isFlipped ? 'rotate-y-180' : ''
                    }`}
                  >
                    {/* Front — read-only ladder + actions */}
                    <div className={`${isFlipped ? 'hidden' : 'block'} backface-hidden`}>
                      <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-b border-gray-100 bg-gray-50/80">
                        <div className="min-w-0">
                          <div className="font-semibold text-gray-900">{d.displayName}</div>
                          {d.systemKeyInUse ? (
                            <p className="text-xs text-amber-800 mt-1">In use by at least one subject in Plan.</p>
                          ) : null}
                        </div>
                        <div className="flex shrink-0 gap-2">
                          <Button type="button" variant="outline" size="sm" onClick={() => startEdit(d.key)}>
                            Edit structure
                          </Button>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            disabled={d.systemKeyInUse === true}
                            title={
                              d.systemKeyInUse
                                ? 'Cannot delete while a subject uses this system.'
                                : 'Delete education system'
                            }
                            onClick={() => void handleDelete(d)}
                          >
                            <Trash2 className="h-4 w-4 text-destructive-action" />
                          </Button>
                        </div>
                      </div>
                      {d.keyStages.length > 0 ? (
                        <div className="space-y-3 p-4">
                          {d.keyStages.map((ks, ksIdx) => (
                            <div
                              key={`${d.key}-${ks.name}-${ksIdx}`}
                              className="border rounded-md p-3 text-sm border-gray-200"
                            >
                              <div className="font-medium text-gray-900">{ks.name}</div>
                              <div className="text-gray-600 mt-1">
                                {ks.years.length > 0 ? (
                                  ks.years.map((y) => formatYearWithTypicalAges(y)).join(' · ')
                                ) : (
                                  <span className="italic text-gray-500">No years yet</span>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="px-4 py-4 text-sm text-gray-500">
                          No key stages or years yet. Use Edit structure to define levels (same idea as the UK
                          National Curriculum reference below).
                        </div>
                      )}
                    </div>

                    {/* Back — editor */}
                    <div
                      className={`${
                        isFlipped ? 'block' : 'hidden'
                      } rotate-y-180 backface-hidden bg-white rounded-lg border border-gray-200`}
                    >
                      {isFlipped && (
                        <div className="p-4 space-y-6">
                          <div className="flex flex-wrap items-start justify-between gap-3 border-b border-gray-100 pb-4">
                            <div className="min-w-0 flex-1 max-w-md">
                              <h3 className="text-lg font-semibold text-gray-900">Edit system</h3>
                              <label className="text-sm font-medium text-gray-700 mt-3 block" htmlFor={`edu-sys-name-${d.key}`}>
                                System name
                              </label>
                              <Input
                                id={`edu-sys-name-${d.key}`}
                                className="mt-1"
                                value={editDisplayName}
                                onChange={(e) => setEditDisplayName(e.target.value)}
                                placeholder="e.g. Montessori bands"
                              />
                            </div>
                            <Button type="button" variant="ghost" size="sm" onClick={cancelEdit}>
                              <ArrowLeft className="w-4 h-4 mr-2" />
                              Back to view
                            </Button>
                          </div>
                          <p className="text-sm text-gray-600">
                            Key stages can be saved with no years; add years when you are ready. Year labels must
                            match what you choose in custom subject dialogs (e.g. Year 7). Optional typical ages
                            (min and/or max) help lesson generation pitch content to that year group — not the
                            learner&apos;s date of birth. You cannot remove placements that still have topics in
                            a student&apos;s plan.
                          </p>
                          {editBands.map((band, bandIdx) => {
                            const stageBlocked = placementListHasKeyStage(
                              editingDef,
                              editingUsagePlacements,
                              band.name
                            );
                            return (
                              <div key={band.clientId} className="border rounded-lg p-4 space-y-3 border-gray-200">
                                <div className="flex flex-wrap items-start gap-2 justify-between">
                                  <div className="flex-1 min-w-[12rem]">
                                    <label className="text-sm font-medium text-gray-700">Key stage name</label>
                                    <Input
                                      value={band.name}
                                      onChange={(e) => updateBandName(bandIdx, e.target.value)}
                                      placeholder="e.g. Lower Elementary"
                                    />
                                  </div>
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    className="shrink-0"
                                    disabled={stageBlocked}
                                    title={
                                      stageBlocked
                                        ? 'This key stage has topics in use under this system and cannot be removed.'
                                        : 'Remove key stage'
                                    }
                                    onClick={() => removeBandRow(bandIdx)}
                                  >
                                    <Trash2 className="h-4 w-4 text-destructive-action" />
                                  </Button>
                                </div>

                                <div className="space-y-2">
                                  <div className="text-sm font-medium text-gray-700">Years</div>
                                  {band.years.map((yr, yearIdx) => {
                                    const yearLocked =
                                      yr.label.trim().length > 0 &&
                                      placementListHasPair(
                                        editingDef,
                                        editingUsagePlacements,
                                        band.name,
                                        yr.label
                                      );
                                    const canRemoveYear = !placementListHasPair(
                                      editingDef,
                                      editingUsagePlacements,
                                      band.name,
                                      yr.label
                                    );
                                    return (
                                      <div key={yr.clientId} className="flex flex-wrap items-center gap-2">
                                        <Input
                                          className="flex-1 min-w-[10rem]"
                                          value={yr.label}
                                          disabled={yearLocked}
                                          title={
                                            yearLocked
                                              ? 'This year has topics in a student plan; the label cannot be changed here.'
                                              : undefined
                                          }
                                          onChange={(e) => updateYearLabel(bandIdx, yearIdx, e.target.value)}
                                          placeholder="e.g. Year 3"
                                        />
                                        <Input
                                          className="w-20"
                                          type="number"
                                          min={1}
                                          max={25}
                                          inputMode="numeric"
                                          value={yr.typicalAgeMinYears ?? ''}
                                          onChange={(e) =>
                                            updateYearAge(bandIdx, yearIdx, 'typicalAgeMinYears', e.target.value)
                                          }
                                          placeholder="Age min"
                                          title="Optional typical age min for this year group"
                                        />
                                        <Input
                                          className="w-20"
                                          type="number"
                                          min={1}
                                          max={25}
                                          inputMode="numeric"
                                          value={yr.typicalAgeMaxYears ?? ''}
                                          onChange={(e) =>
                                            updateYearAge(bandIdx, yearIdx, 'typicalAgeMaxYears', e.target.value)
                                          }
                                          placeholder="Age max"
                                          title="Optional typical age max for this year group"
                                        />
                                        <Button
                                          type="button"
                                          variant="outline"
                                          size="sm"
                                          disabled={!canRemoveYear}
                                          title={
                                            !canRemoveYear
                                              ? 'Cannot remove a year that still has topics in a student plan.'
                                              : 'Remove year'
                                          }
                                          onClick={() => removeYearRow(bandIdx, yearIdx)}
                                        >
                                          <Trash2 className="h-4 w-4" />
                                        </Button>
                                      </div>
                                    );
                                  })}
                                  <Button type="button" variant="outline" size="sm" onClick={() => addYearRow(bandIdx)}>
                                    <Plus className="h-4 w-4 mr-1" />
                                    Add year
                                  </Button>
                                </div>
                              </div>
                            );
                          })}

                          <Button type="button" variant="outline" size="sm" onClick={addBandRow}>
                            <Plus className="h-4 w-4 mr-1" />
                            Add key stage
                          </Button>

                          <div className="flex flex-wrap gap-2 pt-2">
                            <Button type="button" onClick={() => void saveEdit()}>
                              Save changes
                            </Button>
                            <Button type="button" variant="ghost" onClick={cancelEdit}>
                              Cancel
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        </CardContent>
      </Card>

      {builtInFrameworks.map((fw) => (
        <Card key={fw.key}>
          <CardHeader>
            <CardTitle>{fw.displayName}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {fw.keyStages.map((ks) => (
              <div key={ks.name} className="border rounded-md p-3 text-sm">
                <div className="font-medium text-gray-900">{ks.name}</div>
                <div className="text-gray-600 mt-1">
                  {ks.years.map((y) => formatYearWithTypicalAges(y)).join(' · ')}
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
